import { SealCheckIcon } from "@phosphor-icons/react/dist/csr/SealCheck";
import { UserPlusIcon } from "@phosphor-icons/react/dist/csr/UserPlus";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { UsersThreeIcon } from "@phosphor-icons/react/dist/csr/UsersThree";
import { Card } from "@volca/ui/components/ui/card";
import { StatTile } from "@volca/ui/components/ui/stat-tile";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  useGetBusinessStats,
  useGetBusinessWeeklyStats,
} from "@/business/query";
import { useBusiness } from "@/core/business";
import { getPeriodDelta, getPeriodTotals } from "./get-period-totals";

/**
 * Four headline numbers: the last four weeks against the four before.
 * `isCompact` is for when a side panel narrows the page: one column on a
 * tablet, two by two on a desktop.
 */
export const PerformanceStats = ({
  isCompact = false,
}: {
  isCompact?: boolean;
}) => {
  const { businessId } = useBusiness();
  const weekly = useGetBusinessWeeklyStats(businessId);
  const allTime = useGetBusinessStats(businessId);

  if (weekly.isError) {
    return (
      <Card className="w-full gap-0 px-4 py-6">
        <Text tone="destructive">
          Couldn&apos;t load your performance. Please try again.
        </Text>
      </Card>
    );
  }

  const stats = weekly.data ?? [];
  const referrals = getPeriodTotals(stats, "referral_count");
  const signups = getPeriodTotals(stats, "total_signup_count");
  const attributed = getPeriodTotals(stats, "attributed_signup_count");

  return (
    <Card className="w-full overflow-hidden py-0">
      <div
        className={cn("grid w-full grid-cols-1 gap-px bg-line", {
          "sm:grid-cols-2 lg:grid-cols-4": !isCompact,
          "lg:grid-cols-2": isCompact,
        })}
      >
        <StatTile
          icon={UserPlusIcon}
          label="Referrals"
          value={referrals.current}
          valueTone="brand"
          delta={getPeriodDelta(referrals)}
          comparison={`vs ${referrals.previous}`}
          isLoading={weekly.isLoading}
        />
        <StatTile
          icon={UsersIcon}
          label="Sign-ups"
          value={signups.current}
          valueTone="brand"
          delta={getPeriodDelta(signups)}
          comparison={`vs ${signups.previous}`}
          isLoading={weekly.isLoading}
        />
        <StatTile
          icon={SealCheckIcon}
          label="Attributed sign-ups"
          value={attributed.current}
          valueTone="brand"
          delta={getPeriodDelta(attributed)}
          comparison={`vs ${attributed.previous}`}
          isLoading={weekly.isLoading}
        />
        <StatTile
          icon={UsersThreeIcon}
          label="Advocates"
          value={allTime.data?.program_signup_count ?? "–"}
          valueTone="brand"
          caption={allTime.isError ? "Couldn't load this number" : "All time"}
          isLoading={allTime.isLoading}
        />
      </div>
    </Card>
  );
};
